import { PublicLayout } from '../layout/PublicLayout';
import { Badge } from '@/components/ui/badge';
import { Link, Navigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import { BlogImage } from '@/components/blog/BlogImage';
import { BLOG_POSTS, SLUG_REDIRECTS } from '../data/blogPosts';

export const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  // Two posts were renamed because their URL carried a word our communication rules forbid in a
  // user-facing string. `replace` so the redirect does not pollute browser history, and the new URL
  // becomes canonical for search engines.
  const redirectTo = slug ? SLUG_REDIRECTS[slug] : undefined;
  if (redirectTo) return <Navigate to={`/blog/${redirectTo}`} replace />;

  useSEO({
    title: post ? `${post.title} | Raisy` : 'Post Not Found | Raisy',
    description: post ? post.excerpt : 'Blog post not found.',
    keywords: post ? post.keywords.join(', ') : '',
    ogTitle: post ? post.title : 'Raisy Blog',
    ogDescription: post ? post.excerpt : 'Raisy Blog',
    ogType: 'article',
    // The cover IS the share image (RULES_BLOG_POST's cover/lead/OG artifact). Without it the card fell
    // back to the generic site image, so every post shared identically — measured 2026-10-09.
    ogImage: post ? `https://raisy.team${post.image}` : undefined,
    canonicalUrl: post ? `https://raisy.team/blog/${post.slug}` : 'https://raisy.team/blog',
  });

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <PublicLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>

        <div className="mb-10">
          {/* Lead figure — cover image, click-to-enlarge (RULES_BLOG_POST) */}
          <BlogImage src={post.image} alt={post.title} caption={`${post.category} · ${post.title}`} />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-500 mb-2">
            {post.series}
          </p>
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/40">
              {post.category}
            </Badge>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {new Date(post.date + 'T00:00:00Z').toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            {post.title}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300">{post.excerpt}</p>
        </div>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 [&>ul]:list-disc [&>ul]:pl-6">
          {post.content}
        </div>

        {post.faq && post.faq.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">FAQ</h2>
            <div className="space-y-4">
              {post.faq.map((f, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-white">{f.q}</h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {post.sources && (
          <div className="mt-10">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Sources</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{post.sources}</p>
          </div>
        )}

        {/* JSON-LD structured data — BlogPosting + FAQPage (AEO/GEO lever) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'BlogPosting',
                  headline: post.title,
                  description: post.excerpt,
                  datePublished: post.date,
                  keywords: post.keywords.join(', '),
                  image: `https://raisy.team${post.image}`,
                  url: `https://raisy.team/blog/${post.slug}`,
                  author: { '@type': 'Organization', name: 'Raisy', url: 'https://raisy.team' },
                  publisher: { '@type': 'Organization', name: 'Raisy', url: 'https://raisy.team' },
                },
                ...(post.faq && post.faq.length > 0
                  ? [
                      {
                        '@type': 'FAQPage',
                        mainEntity: post.faq.map((f) => ({
                          '@type': 'Question',
                          name: f.q,
                          acceptedAnswer: { '@type': 'Answer', text: f.a },
                        })),
                      },
                    ]
                  : []),
              ],
            }),
          }}
        />

        <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Keep reading</p>
          <div className="grid gap-6 sm:grid-cols-2">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all overflow-hidden flex flex-col"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-40 object-cover group-hover:scale-[1.03] transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-cyan-600 dark:text-cyan-400">
                      {p.category}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(p.date + 'T00:00:00Z').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC',
                      })}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-cyan-700 dark:group-hover:text-cyan-400">
                    {p.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </PublicLayout>
  );
};
